import React, { forwardRef, LegacyRef, MouseEvent, Ref } from "react";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { DA_BasicLink } from "@danishagro/shared/src/components/atoms/BasicLink/BasicLink.component";
import { DA_Icon, DA_IconNames } from "@danishagro/shared/src/components/atoms/Icon/Icon.component";
import { DA_Spinner } from "@danishagro/shared/src/components/atoms/Spinner/Spinner.component";
import S from "./Button.module.scss";

export enum DA_ButtonColor {
    White = "White",
    Action = "Action",
    Alert = "Alert",
    Grey = "Grey",
    LightGrey = "LightGrey",
    Primary = "Primary",
    Accent = "Accent",
}

interface ButtonPropsBase {
    title?: string;
    className?: string;
    color?: DA_ButtonColor;
    icon?: DA_IconNames;
    isGhost?: boolean;
    largeText?: boolean;
    small?: boolean;
    large?: boolean;
    form?: string;
    hideText?: boolean;
    noWrap?: boolean;
    iconRight?: boolean;
    showSpinner?: boolean;
    showSpinnerWithTitle?: boolean;
    fullWidth?: boolean;
    disabled?: boolean;
    borderRadiusSize?: "small" | "medium";
    children?: React.ReactNode;
    tabIndex?: number;
}

interface ButtonWithOnClick extends ButtonPropsBase {
    onClick: (event?: MouseEvent<HTMLButtonElement>) => void;
    href?: never;
    target?: never;
    submit?: never;
    conventionalRouting?: never;
    ariaLabel?: string;
}

interface ButtonWithSubmit extends ButtonPropsBase {
    onClick?: never;
    href?: never;
    target?: never;
    submit: true;
    conventionalRouting?: never;
    ariaLabel?: string;
}

interface ButtonWithHref extends ButtonPropsBase {
    onClick?: (event?: MouseEvent<HTMLButtonElement>) => void;
    href: string;
    target?: "_blank" | "_self";
    submit?: never;
    conventionalRouting?: boolean;
    forceNativeUrl?: boolean;
    ariaLabel?: string;
}

export type DA_ButtonProps = ButtonWithOnClick | ButtonWithSubmit | ButtonWithHref;

export const DA_Button = forwardRef<HTMLAnchorElement | HTMLButtonElement, DA_ButtonProps>(
    (props, ref) => {
        const {
            title,
            onClick,
            form,
            color,
            className,
            icon,
            href,
            target,
            isGhost,
            largeText,
            small,
            large,
            hideText,
            fullWidth,
            disabled,
            noWrap,
            submit,
            iconRight,
            conventionalRouting,
            showSpinner,
            showSpinnerWithTitle,
            ariaLabel,
            borderRadiusSize = "small",
            tabIndex,
            children,
        } = props;

        const buttonClass = cn(
            S.button,
            largeText && S.largeText,
            small && S.small,
            large && S.large,
            isGhost && S.ghost,
            iconRight && S.iconRight,
            noWrap && S.noWrap,
            icon && S.hasIcon,
            icon && (!title || hideText) && S.iconOnly,
            fullWidth && S.fullWidth,
            disabled && S.disabled,
            borderRadiusSize === "medium" && S.buttonMediumBorderRadius,
            (color === DA_ButtonColor.Action || (!color && !isGhost)) && S.action,
            color === DA_ButtonColor.White && S.white,
            (color === DA_ButtonColor.Grey || (!color && isGhost)) && S.grey,
            color === DA_ButtonColor.LightGrey && S.lightGrey,
            color === DA_ButtonColor.Primary && S.primary,
            color === DA_ButtonColor.Accent && S.acent,
            color === DA_ButtonColor.Alert && S.alert,
            className
        );

        // Link
        if (href) {
            const linkButtonProps = props as ButtonWithHref;
            const linkProps: Record<string, unknown> = {};

            if (conventionalRouting !== undefined) {
                linkProps.conventionalRouting = conventionalRouting;
            }

            if (linkButtonProps.forceNativeUrl !== undefined) {
                linkProps.forceNativeUrl = linkButtonProps.forceNativeUrl;
            }

            return (
                <DA_BasicLink
                    ref={ref as Ref<HTMLAnchorElement>}
                    href={href}
                    target={target}
                    className={buttonClass}
                    onClick={onClick as never}
                    ariaLabel={ariaLabel || (hideText && title)}
                    {...linkProps}
                    tabIndex={tabIndex}
                >
                    {/** Spinner - No Icon */}
                    {showSpinner && !icon && (
                        <span className={S.spinner}>
                            <DA_Spinner />
                        </span>
                    )}

                    {showSpinnerWithTitle && (
                        <span className={cn(S.content, S.showSpinnerWithTitle)}>
                            <DA_Spinner className={S.icon} />

                            {/** Title */}
                            {title && !hideText && <span className={S.title}>{title}</span>}
                        </span>
                    )}

                    <span className={S.content}>
                        {/** Icon - No Spinner */}
                        {icon && !showSpinner ? (
                            <DA_Icon name={icon} className={S.icon} />
                        ) : (
                            icon && showSpinner && <DA_Spinner className={S.icon} />
                        )}

                        {/** Title */}
                        {title && !hideText && <span className={S.title}>{title}</span>}
                    </span>
                </DA_BasicLink>
            );
        }

        // Button
        else {
            return (
                <button
                    ref={ref as LegacyRef<HTMLButtonElement>}
                    className={buttonClass}
                    type={submit ? "submit" : "button"}
                    onClick={onClick}
                    form={form}
                    disabled={disabled || showSpinner}
                    aria-label={ariaLabel}
                    tabIndex={tabIndex}
                >
                    {/** Spinner - No Icon */}
                    {showSpinner && !icon && (
                        <span className={S.spinner}>
                            <DA_Spinner />
                        </span>
                    )}

                    {showSpinnerWithTitle && (
                        <span className={cn(S.content, S.showSpinnerWithTitle)}>
                            <DA_Spinner className={S.icon} />
                        </span>
                    )}

                    <span className={S.content}>
                        {/** Icon - No Spinner */}
                        {icon && (!showSpinner || !showSpinnerWithTitle) ? (
                            <DA_Icon name={icon} className={S.icon} />
                        ) : (
                            icon && showSpinner && <DA_Spinner className={S.icon} />
                        )}

                        {/** Title */}
                        {title && !hideText && <span className={S.title}>{title}</span>}
                        {children && children}
                    </span>
                </button>
            );
        }
    }
);

DA_Button.displayName = "DA_Button";
